import {
  PlatformRequirementDocumentVerdict,
  PlatformRequirementIssueStatus,
  PlatformRequirementProblemType,
} from '@client/graphql/__generated__/types';

/**
 * Presentational vocabulary shared by the checklist-issue surfaces.
 *
 * A leaf module by necessity, not preference: `ChecklistIssuesList` renders
 * `ChecklistIssueTooltipContent`, and the evidence view needs these same types
 * and tones. Defining them here keeps the two from importing each other. Mirrors
 * the reasoning already recorded in `platformRequirementIssueStatus.format.ts`.
 *
 * `ChecklistIssuesList` re-exports everything here, so existing importers can
 * keep pointing at the component.
 */

/**
 * Whether platform-requirement evaluation has produced data for the selected
 * checklist item. Drives the difference between an honest "no data" state (no
 * evidence evaluation ran) and a positive clean state.
 *
 * - `not_evaluated`: no evaluation evidence — the persisted view shows this
 *   whenever there are no current problems, since it cannot prove clean.
 * - `in_progress`: a live re-evaluation is running (internal live view only).
 * - `evaluated`: evaluation completed; a "No mismatch" clean state is truthful.
 * - `unavailable`: evaluation failed or errored.
 */
export type ChecklistEvaluationStatus =
  | 'not_evaluated'
  | 'in_progress'
  | 'evaluated'
  | 'unavailable';

/** A contract document identified within an attached document. */
export interface ChecklistContractDocumentView {
  form: string | null;
  documentCategory: string | null;
}

/** A document evaluated for a checklist item, with mismatch + contract-doc context. */
export interface ChecklistEvaluationDocumentView {
  documentName: string;
  isProblemDocument: boolean;
  /**
   * The verdict this document was given. Real evidence on both surfaces: the
   * live view supplies it directly, and the persisted view carries the verdict
   * recorded against each `recordedEvaluationDocuments` entry. Absent only when
   * a caller has no verdict to give.
   */
  verdict?: PlatformRequirementDocumentVerdict | null;
  ingestionDocumentId?: string | null;
  platformDocumentId?: string | null;
  contractDocuments: ChecklistContractDocumentView[];
}

/** An evaluation document carrying the stable key its group assigned it. */
export interface ChecklistGroupedEvaluationDocumentView
  extends ChecklistEvaluationDocumentView {
  renderKey: string;
}

/**
 * A checklist document shown as context when the selected checklist item has no
 * current issue (the "None right now" empty state on the internal drawer).
 */
export interface ChecklistContextDocumentView {
  id: string;
  documentName: string;
  hasCurrentProblem: boolean;
  /** Live verdict, when known (internal live view); absent on the persisted view. */
  verdict?: PlatformRequirementDocumentVerdict | null;
  /** Identified form/category for this attachment (internal live view). */
  contractDocuments?: ChecklistContractDocumentView[];
}

/** One projected checklist mismatch issue. Surfaces map their fragment into this. */
export interface ChecklistIssueView {
  publicIssueId: string;
  status: PlatformRequirementIssueStatus;
  /**
   * What kind of problem this is. Rendered as the short reason beside the
   * checklist item name on the compact surfaces — see
   * `formatPlatformRequirementProblemType`.
   */
  problemType: PlatformRequirementProblemType;
  groupKey: string;
  /**
   * Evaluation run that produced this issue. Persisted issues carry the backing
   * problem's run id; the live preview uses the sentinel `'live'`. Rows from
   * different runs must never be merged into one group.
   */
  evaluationRunId: string;
  /** When the recorded evaluation ran. Null for the live preview. */
  evaluatedAt: unknown;
  isAgentActionable: boolean;
  checklistItemName: string;
  checklistSectionName?: string | null;
  allowedDocumentCategories: string[];
  allowedForms: string[];
  /** Documents evaluated in this run, from live or persisted evidence. */
  evaluationDocuments: ChecklistEvaluationDocumentView[];
  /**
   * Free-text reason given when this issue was ignored (the hiding review's
   * `message`). Shown with the crossed-out checklist item's evidence so the
   * reviewer sees the rationale. Undefined/null for open issues and for ignores
   * made without a reason.
   */
  ignoreReason?: string | null;
}

export type DocumentBadgeTone = 'error' | 'success' | 'neutral';

export const DOCUMENT_BADGE_TONE_STYLE: Record<
  DocumentBadgeTone,
  { bg: string; color: string; border?: string }
> = {
  error: {
    bg: 'compliance.notification.error.bg',
    color: 'compliance.notification.error.text',
  },
  success: {
    bg: 'compliance.notification.success.bg',
    color: 'compliance.notification.success.text',
  },
  neutral: {
    bg: 'transparent',
    color: 'compliance.text.muted',
    border: 'compliance.document.border',
  },
};
